import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { Download, FileText, Share2, Zap, MousePointerClick, Star } from "lucide-react";
import { WHATSAPP_DISPLAY } from "../lib/whatsapp";

const API = process.env.REACT_APP_BACKEND_URL;

const partagerAffiche = async (label, file) => {
  const url = `${window.location.origin}/downloads/${file}`;
  const texte = `${label} — FK Conseil & Événement`;
  if (navigator.share) {
    try {
      await navigator.share({ title: "FK Conseil & Événement", text: texte, url });
      return;
    } catch (e) {
      /* annulé par l'utilisateur */
    }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${texte}\n${url}`)}`, "_blank", "noopener,noreferrer");
};

const SLOGAN = "Nous ne plaçons pas simplement du personnel, nous signons l'excellence de votre réception";
const SERVICES = "Serveurs · Serveuses · Maîtres d'hôtel · Hôtesses";
const EVENTS = "Mariages — Vins d'honneur — Conférences — Réceptions privées";
const URGENCE_CHIP = "Imprévu de dernière minute ? Urgence FK Événement — renfort 24h/24, sur place en moins d'une heure.";
const QUALITE = "Un personnel élégant, ponctuel et expérimenté, dirigé par un maître d'hôtel issu des palaces parisiens.";
const TITLE = <>L'art du service <em className="italic text-[#c9a227]">d'exception</em></>;
const FOIL = "bg-gradient-to-r from-[#8a6d1f] via-[#e9cf8a] to-[#c9a227] bg-clip-text text-transparent";

const SparklesGold = ({ count = 10 }) => (
  <>
    {[...Array(count)].map((_, i) => (
      <span key={i} className="pointer-events-none absolute z-20 h-1.5 w-1.5 rounded-full bg-[#d4af37]"
        style={{ left: `${(i * 41 + 9) % 100}%`, top: `${(i * 29 + 4) % 70}%`, animation: `floaty ${3 + (i % 4)}s ease-in-out ${i * 0.3}s infinite`, boxShadow: "0 0 10px 3px rgba(212,175,55,0.65)" }} />
    ))}
  </>
);

/* Pied d'affiche uniforme : devis en ligne → site web (une ligne) → WhatsApp en dernier */
const FooterContact = ({ badgeClass = "!w-full whitespace-nowrap !px-2 !text-[15px]", lineClass = "text-[9px]" }) => (
  <div className="text-center">
    <p className={`${lineClass} font-bold uppercase tracking-[0.16em] text-[#8a6d1f]`} data-testid="affiche-devis">Devis en ligne sur notre site web</p>
    <div className="mt-1.5"><SiteBadge className={badgeClass} /></div>
    <p className={`mt-1.5 ${lineClass} font-bold uppercase tracking-[0.16em] text-[#8a6d1f]`} data-testid="affiche-whatsapp">WhatsApp : {WHATSAPP_DISPLAY}</p>
  </div>
);

/* Rangée de 5 étoiles dorées (slogan 5 étoiles) */
const StarsRow = ({ starClass = "h-3 w-3" }) => (
  <div className="flex items-center justify-center gap-1" data-testid="affiche-etoiles">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${starClass} fill-[#c9a227] text-[#c9a227]`} />
    ))}
  </div>
);

const SiteBadge = ({ className = "" }) => (
  <span data-testid="affiche-site-link" className={`inline-block rounded-full bg-gradient-to-r from-[#c9a227] via-[#e9cf8a] to-[#c9a227] px-6 py-1.5 font-cormorant text-xl font-bold tracking-tight text-[#0a0a0a] shadow-[0_4px_18px_rgba(201,162,39,0.45)] ${className}`}>
    www.fk-evenement.fr
  </span>
);

const UrgenceChip = () => (
  <div className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#c9a227]/50 bg-[#c9a227]/10 px-4 py-2" data-testid="affiche-urgence">
    <Zap className="h-3.5 w-3.5 shrink-0 animate-pulse text-[#aa8c2c]" />
    <p className="text-[9px] font-semibold leading-snug text-[#8a6d1f]">{URGENCE_CHIP}</p>
  </div>
);

const BrandPill = () => (
  <p className="rounded-full bg-white/90 px-5 py-1.5 font-cormorant text-[12px] font-semibold uppercase tracking-[0.4em] text-[#aa8c2c] shadow-lg backdrop-blur-sm">
    FK Conseil &amp; Événement
  </p>
);

const ProBadge = ({ className = "" }) => (
  <p className={`rounded-full bg-[#c9a227] px-4 py-1.5 text-[8px] font-bold uppercase tracking-[0.22em] text-white shadow-lg ${className}`} data-testid="affiche-pro-badge" style={{ display: "inline-block" }}>
    Aux professionnels de l'événementiel
  </p>
);

const BottomVeil = ({ children, from = "40%" }) => (
  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#faf6ec] via-[#faf6ec]/95 to-transparent px-6 pb-4 pt-28 text-center" style={{ clipPath: "none" }}>
    {children}
  </div>
);

/* ========== 1 — FÊTE DORÉE (public, équipe pleine page) ========== */
const PosterCercle = () => (
  <div className="affiche-capture relative h-[780px] w-[440px] overflow-hidden bg-[#faf6ec]">
    <img src="/images/equipe-mixte.png" alt="Brigade FK" className="kenburns absolute inset-0 h-full w-full object-cover" data-testid="affiche-photo" />
    <SparklesGold count={12} />
    <div className="absolute inset-x-0 top-0 z-10 flex justify-center pt-4"><BrandPill /></div>
    <BottomVeil>
      <h1 className="font-cormorant text-[2.5rem] font-semibold leading-[0.95] tracking-tight text-[#0a0a0a]" data-testid="affiche-art">{TITLE}</h1>
      <div className="mt-2"><StarsRow /></div>
      <p className="mt-1 font-cormorant text-[1.1rem] font-medium italic leading-snug text-[#aa8c2c]" data-testid="affiche-slogan">« {SLOGAN} »</p>
      <p className="mt-2 text-[8.5px] font-semibold uppercase tracking-[0.22em] text-[#aa8c2c]">{SERVICES}</p>
      <p className="mt-0.5 text-[8.5px] uppercase tracking-[0.16em] text-[#1a1a1a]/55">{EVENTS}</p>
      <div className="mt-2.5"><UrgenceChip /></div>
      <div className="mt-2"><FooterContact badgeClass="!w-full whitespace-nowrap !px-3 !py-1 !text-base" /></div>
    </BottomVeil>
  </div>
);

/* ========== 2 — DUO ÉCLAT (public, hôtesse pleine page) ========== */
/* ========== 2 — SPLIT ÉDITORIAL (public, validé par le client) ========== */
const PosterSplit = ({ photo = "/images/hotesse.png", alt = "Hôtesse FK" }) => (
  <div className="affiche-capture relative flex h-[780px] w-[440px] overflow-hidden bg-[#faf6ec]">
    <div className="relative h-full w-[225px] shrink-0">
      <img src={photo} alt={alt} className="kenburns h-full w-full object-cover" data-testid="affiche-photo" />
      <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#faf6ec]/80 to-transparent" />
    </div>
    <div className="absolute inset-y-0 left-[225px] z-10 w-[3px] bg-gradient-to-b from-transparent via-[#c9a227] to-transparent shadow-[0_0_14px_2px_rgba(201,162,39,0.5)]" />
    <div className="relative flex flex-1 flex-col px-5 py-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#e9cf8a] to-[#c9a227] font-cormorant text-lg font-bold text-white shadow-lg ring-2 ring-[#c9a227]/40">FK</div>
      <p className="mt-2 font-cormorant text-[11px] font-semibold uppercase tracking-[0.3em] text-[#aa8c2c]">Conseil &amp; Événement</p>
      <h1 className="mt-2 font-cormorant text-[1.85rem] font-semibold leading-[1] tracking-tight text-[#0a0a0a]" data-testid="affiche-art">{TITLE}</h1>
      <p className="mt-2 text-[9px] font-semibold uppercase leading-loose tracking-[0.18em] text-[#aa8c2c]">{SERVICES}</p>
      <p className="mt-1 text-[8px] uppercase leading-loose tracking-[0.14em] text-[#1a1a1a]/55">{EVENTS}</p>
      <p className="mt-2 font-cormorant text-[1.05rem] font-medium italic leading-snug text-[#aa8c2c]" data-testid="affiche-slogan">« {SLOGAN} »</p>
      <div className="mt-2 flex items-center gap-2">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#c9a227]" />
        <span className="h-1.5 w-1.5 rotate-45 bg-[#c9a227]" />
        <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#c9a227]" />
      </div>
      <p className="mt-2 text-[9.5px] leading-relaxed text-[#1a1a1a]/70">{QUALITE}</p>
      <p className="mt-1 text-[8px] font-semibold uppercase tracking-[0.18em] text-[#aa8c2c]">Île-de-France &amp; toute l'Europe</p>
      <div className="mt-2 flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="h-3 w-3 fill-[#c9a227] text-[#c9a227]" />
        ))}
      </div>
      <p className="mt-1 font-cormorant text-[12px] font-semibold italic leading-snug text-[#0a0a0a]/85">
        Un service cinq étoiles, digne des plus grands palaces, pour vos événements.
      </p>
      <div className="mt-auto">
        <UrgenceChip />
        <div className="mt-2 border-t border-[#c9a227]/40 pt-2">
          <FooterContact />
        </div>
      </div>
    </div>
  </div>
);

/* ========== 3 — DUO DE SERVEURS (public, pleine page, visages + service visibles) ========== */
const PosterDuoVisages = ({ photo = "/images/duo-visages.png", alt = "Duo de serveurs FK en service" }) => (
  <div className="affiche-capture relative h-[780px] w-[440px] overflow-hidden bg-[#faf6ec]">
    <img src={photo} alt={alt} className="kenburns absolute inset-0 h-full w-full object-cover" data-testid="affiche-photo" />
    <div className="absolute inset-x-0 top-0 z-10 flex justify-center pt-4"><BrandPill /></div>
    <div className="absolute inset-x-0 bottom-0 text-center">
      <div className="h-8 bg-gradient-to-t from-[#faf6ec] to-transparent" />
      <div className="bg-[#faf6ec] px-6 pb-4">
        <SparklesGold count={8} />
        <h1 className="font-cormorant text-[2.1rem] font-semibold leading-[0.95] tracking-tight text-[#0a0a0a]" data-testid="affiche-art">{TITLE}</h1>
        <div className="mt-1.5"><StarsRow /></div>
        <p className="mt-1 font-cormorant text-[1rem] font-semibold italic leading-snug text-[#8a6d1f]" data-testid="affiche-slogan">« {SLOGAN} »</p>
        <p className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#8a6d1f]">{SERVICES}</p>
        <p className="mt-0.5 text-[9.5px] font-semibold uppercase tracking-[0.14em] text-[#1a1a1a]/75">{EVENTS}</p>
        <div className="mt-2"><UrgenceChip /></div>
        <div className="mt-2 border-t border-[#c9a227]/40 pt-2">
          <FooterContact badgeClass="!w-full whitespace-nowrap !px-2 !text-[15px]" lineClass="text-[9px]" />
        </div>
      </div>
    </div>
  </div>
);

/* ========== 4 — PLEINE PAGE FÊTE (public, duo champagne) ========== */
const PosterPleinePage = () => (
  <div className="affiche-capture relative h-[780px] w-[440px] overflow-hidden bg-[#faf6ec]">
    <img src="/images/duo-champagne.png" alt="Duo champagne FK" className="kenburns absolute inset-0 h-full w-full object-cover" data-testid="affiche-photo" />
    <div className="absolute inset-x-0 top-0 z-10 flex justify-center pt-4"><BrandPill /></div>
    <BottomVeil>
      <SparklesGold count={8} />
      <h1 className="font-cormorant text-[2.5rem] font-semibold leading-[0.95] tracking-tight text-[#0a0a0a]" data-testid="affiche-art">{TITLE}</h1>
      <div className="mt-2"><StarsRow /></div>
      <p className="mt-1 font-cormorant text-[1.1rem] font-medium italic leading-snug text-[#aa8c2c]" data-testid="affiche-slogan">« {SLOGAN} »</p>
      <p className="mt-2 text-[8.5px] font-semibold uppercase tracking-[0.22em] text-[#aa8c2c]">{SERVICES}</p>
      <p className="mt-0.5 text-[8.5px] uppercase tracking-[0.16em] text-[#1a1a1a]/55">{EVENTS}</p>
      <div className="mt-2.5"><UrgenceChip /></div>
      <div className="mt-2"><FooterContact badgeClass="!w-full whitespace-nowrap !px-3 !py-1 !text-base" /></div>
    </BottomVeil>
  </div>
);

/* ========== 4 — SALLE ROYALE (pro, carte ivoire flottante) ========== */
const PosterRoyale = () => (
  <div className="affiche-capture relative h-[780px] w-[440px] overflow-hidden bg-[#faf6ec]">
    <img src="/images/salle-moderne1.png" alt="Dîner sous verrière" className="kenburns absolute inset-0 h-full w-full object-cover" data-testid="affiche-photo" />
    <div className="absolute inset-x-0 top-0 z-10 flex justify-center pt-4"><BrandPill /></div>
    <div className="absolute inset-x-4 bottom-4 rounded-3xl border border-[#c9a227]/60 bg-[#faf6ec]/95 px-6 py-5 text-center shadow-2xl backdrop-blur-sm">
      <SparklesGold count={6} />
      <ProBadge />
      <h1 className="mt-2.5 font-cormorant text-[2rem] font-semibold leading-[0.95] tracking-tight text-[#0a0a0a]" data-testid="affiche-art">{TITLE}</h1>
      <div className="mt-2"><StarsRow /></div>
      <p className="mt-1 font-cormorant text-[1.05rem] font-medium italic leading-snug text-[#aa8c2c]" data-testid="affiche-slogan">« {SLOGAN} »</p>
      <p className="mt-2 text-[8.5px] font-semibold uppercase tracking-[0.22em] text-[#aa8c2c]">{SERVICES}</p>
      <p className="mt-0.5 text-[8px] uppercase tracking-[0.16em] text-[#1a1a1a]/55">Île-de-France &amp; toute l'Europe</p>
      <div className="mt-2.5"><UrgenceChip /></div>
      <div className="mt-2.5"><FooterContact badgeClass="!w-full whitespace-nowrap !px-3 !py-1 !text-lg" lineClass="text-[9.5px]" /></div>
    </div>
  </div>
);

/* Bloc slogan 5 étoiles complet */
const EtoilesSlogan = () => (
  <div className="flex flex-col items-center">
    <StarsRow />
    <p className="mt-1 font-cormorant text-[12.5px] font-semibold italic leading-snug text-[#0a0a0a]/85" data-testid="affiche-5etoiles">
      Un service cinq étoiles, digne des plus grands palaces, pour vos événements.
    </p>
  </div>
);

/* ========== 5 — COUVERTURE MAGAZINE (pro, serveur noir, photo complète + texte ivoire) ========== */
const PosterMagazine = ({ photo = "/images/serveur-noir.png", alt = "Serveur FK en service" }) => (
  <div className="affiche-capture relative flex h-[780px] w-[440px] flex-col overflow-hidden bg-[#faf6ec]">
    <div className="relative h-[492px] w-full shrink-0 overflow-hidden">
      <img src={photo} alt={alt} className="kenburns h-full w-full object-cover object-[50%_15%]" data-testid="affiche-photo" />
      <div className="absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-white/90 via-white/45 to-transparent px-6 pb-10 pt-3 text-center">
        <p className={`font-cormorant text-[2.6rem] font-bold leading-none tracking-tight ${FOIL}`}>FK</p>
        <p className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.5em] text-[#0a0a0a]">Conseil &amp; Événement</p>
      </div>
      <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#faf6ec] to-transparent" />
    </div>
    <div className="flex flex-1 flex-col items-center px-6 pb-2 text-center">
      <p className="mt-0.5 font-cormorant text-[0.95rem] font-semibold italic leading-snug text-[#0a0a0a]" data-testid="affiche-slogan">
        « Nous ne plaçons pas simplement du personnel, <span className={FOIL}>nous signons l'excellence de votre réception</span> »
      </p>
      <div className="mt-1 h-[3px] w-full bg-gradient-to-r from-[#c9a227] via-[#e9cf8a] to-[#c9a227]" />
      <h1 className="mt-1 font-cormorant text-[1.6rem] font-semibold leading-none tracking-tight text-[#0a0a0a]" data-testid="affiche-art">{TITLE}</h1>
      <p className="mt-0.5 text-[8.5px] font-semibold uppercase tracking-[0.22em] text-[#aa8c2c]">{SERVICES}</p>
      <p className="text-[8px] uppercase tracking-[0.16em] text-[#1a1a1a]/55">{EVENTS}</p>
      <div className="mt-1 w-full"><UrgenceChip /></div>
      <div className="mt-1.5"><EtoilesSlogan /></div>
      <div className="mt-1.5 w-full"><FooterContact badgeClass="!w-full whitespace-nowrap !px-3 !py-0.5 !text-[15px]" lineClass="text-[8.5px]" /></div>
    </div>
  </div>
);

const VARIANTS = [
  { id: "public-editorial", label: "Public 1 — Fête Dorée", Poster: PosterCercle },
  { id: "public-couture", label: "Public 2 — Duo Éclat", Poster: PosterSplit },
  { id: "public-duo", label: "Public 3 — Split Service en Duo", Poster: () => <PosterSplit photo="/images/duo-service.png" alt="Service en duo FK" /> },
  { id: "public-duo-visages", label: "Public 4 — Duo de Serveurs", Poster: PosterDuoVisages },
  { id: "public-serveuse", label: "Public 5 — Serveuse Élégante", Poster: () => <PosterDuoVisages photo="/images/serveuse-elegante.png" alt="Serveuse FK en service" /> },
  { id: "public-artdeco", label: "Public 6 — Pleine Page Fête", Poster: PosterPleinePage },
  { id: "pro-architecte", label: "Pro 1 — Salle Royale", Poster: PosterRoyale },
  { id: "pro-cinematique", label: "Pro 2 — Couverture Magazine", Poster: PosterMagazine },
  { id: "pro-maitre", label: "Pro 3 — Maître d'Hôtel", Poster: () => <PosterMagazine photo="/images/maitre-hotel.png" alt="Maître d'hôtel FK" /> },
];

/* ========== FORMATS CARRÉS INSTAGRAM (540x540 → export 1080x1080) ========== */
const PosterCarre = ({ photo, alt }) => (
  <div className="affiche-capture relative flex h-[540px] w-[540px] flex-col overflow-hidden bg-[#faf6ec]">
    <div className="relative h-[290px] w-full shrink-0 overflow-hidden">
      <img src={photo} alt={alt} className="h-full w-full object-cover object-[50%_18%]" data-testid="affiche-photo" />
      <div className="absolute inset-x-0 top-0 z-10 flex justify-center pt-3"><BrandPill /></div>
      <div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-[#faf6ec] to-transparent" />
    </div>
    <div className="flex flex-1 flex-col items-center px-5 pb-2.5 text-center">
      <h1 className="font-cormorant text-[1.5rem] font-semibold leading-none tracking-tight text-[#0a0a0a]" data-testid="affiche-art">{TITLE}</h1>
      <div className="mt-1.5"><EtoilesSlogan /></div>
      <p className="mt-1 text-[8px] font-bold uppercase tracking-[0.2em] text-[#8a6d1f]">{SERVICES}</p>
      <p className="mt-0.5 text-[7.5px] uppercase tracking-[0.14em] text-[#1a1a1a]/55">{EVENTS}</p>
      <div className="mt-1.5 w-full"><UrgenceChip /></div>
      <div className="mt-auto w-full pt-1.5"><FooterContact badgeClass="!w-full whitespace-nowrap !px-2 !py-0.5 !text-[14px]" lineClass="text-[8px]" /></div>
    </div>
  </div>
);

const CARRES = [
  { id: "carre-duo", label: "Instagram — Duo de Serveurs", Poster: () => <PosterCarre photo="/images/duo-visages.png" alt="Duo de serveurs FK" />, width: 540, height: 540 },
  { id: "carre-serveur", label: "Instagram — Serveur", Poster: () => <PosterCarre photo="/images/serveur-noir.png" alt="Serveur FK" />, width: 540, height: 540 },
  { id: "carre-serveuse", label: "Instagram — Serveuse", Poster: () => <PosterCarre photo="/images/serveuse-elegante.png" alt="Serveuse FK" />, width: 540, height: 540 },
  { id: "carre-maitre", label: "Instagram — Maître d'Hôtel", Poster: () => <PosterCarre photo="/images/maitre-hotel.png" alt="Maître d'hôtel FK" />, width: 540, height: 540 },
  { id: "carre-equipe", label: "Instagram — Brigade FK", Poster: () => <PosterCarre photo="/images/equipe-mixte.png" alt="Brigade FK" />, width: 540, height: 540 },
  { id: "carre-hotesse", label: "Instagram — Duo Éclat", Poster: () => <PosterCarre photo="/images/hotesse.png" alt="Duo éclat FK" />, width: 540, height: 540 },
  { id: "carre-salle", label: "Instagram — Salle Royale", Poster: () => <PosterCarre photo="/images/salle-moderne1.png" alt="Salle royale" />, width: 540, height: 540 },
];

/* Adapte l'affiche à la largeur de l'écran (mobile inclus) */
const ScaleToFit = ({ children, width = 440, height = 780 }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / width));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [width]);
  return (
    <div ref={ref} className="w-full" style={{ maxWidth: width, aspectRatio: `${width} / ${height}` }}>
      <div style={{ width, height, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        {children}
      </div>
    </div>
  );
};

const PosterBlock = ({ id, label, Poster, width = 440, height = 780 }) => {
  const ref = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    if (!ref.current || downloading) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(ref.current.querySelector(".affiche-capture"), { pixelRatio: 3, cacheBust: true });
      const link = document.createElement("a");
      link.download = `affiche-fk-evenement-${id}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      // silencieux
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="flex flex-col items-center" data-testid={`affiche-block-${id}`}>
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">{label}</p>
      <a
        href="https://fk-evenement.fr"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`affiche-click-${id}`}
        className="block w-full cursor-pointer overflow-hidden rounded-xl shadow-2xl transition-transform duration-300 hover:scale-[1.02]"
        style={{ maxWidth: width }}
        title="Cliquez pour visiter fk-evenement.fr"
      >
        <div ref={ref} className="pointer-events-none w-full">
          <ScaleToFit width={width} height={height}>
            <Poster />
          </ScaleToFit>
        </div>
      </a>
      <a
        href="https://fk-evenement.fr"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`affiche-visit-${id}`}
        className="mt-4 flex items-center gap-2 rounded-full border-2 border-gold px-7 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
      >
        <MousePointerClick className="h-4 w-4" />
        Visiter www.fk-evenement.fr
      </a>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        data-testid={`affiche-download-${id}`}
        className="mt-4 flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50"
      >
        <Download className="h-4 w-4" />
        {downloading ? "Téléchargement..." : "Télécharger cette affiche"}
      </button>
      <div className="mt-3 flex items-center gap-3">
        <a
          href={`${API}/api/affiche-pdf/affiche-fk-evenement-${id}.png`}
          data-testid={`affiche-pdf-${id}`}
          className="flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:border-gold hover:text-gold"
        >
          <FileText className="h-3.5 w-3.5" />
          PDF
        </a>
        <button
          type="button"
          onClick={() => partagerAffiche(label, `affiche-fk-evenement-${id}.png`)}
          data-testid={`affiche-share-${id}`}
          className="flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:border-gold hover:text-gold"
        >
          <Share2 className="h-3.5 w-3.5" />
          Partager
        </button>
      </div>
    </div>
  );
};

export default function AffichesFK() {
  const [searchParams] = useSearchParams();
  const seule = searchParams.get("seule");
  const solo = seule ? [...VARIANTS, ...CARRES].find((x) => x.id === seule) : null;

  if (solo) {
    const Solo = solo.Poster;
    return (
      <div className="flex min-h-screen items-start justify-center bg-[#faf6ec]" data-testid="affiche-seule">
        <Solo />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#141414] px-6 py-20" data-testid="affiches-fk-page">
      <style>{`
        @keyframes floaty { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes kenburns { 0% { transform: scale(1); } 50% { transform: scale(1.06); } 100% { transform: scale(1); } }
        .kenburns { animation: kenburns 18s ease-in-out infinite; }
        .font-cormorant { font-family: 'Cormorant Garamond', 'Fraunces', serif; }
      `}</style>
      <div className="mx-auto max-w-[1600px]">
        <div className="text-center">
          <h1 className="font-display text-4xl tracking-tight text-white sm:text-5xl">
            Affiches <em className="italic text-gold">FK Conseil &amp; Événement</em>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm text-white/50">
            9 affiches haut de gamme et 7 formats carrés pour Instagram, animés et lumineux. Un clic sur l'affiche ouvre le site ;
            téléchargez chaque visuel en image pour vos réseaux sociaux.
          </p>
        </div>
        <div className="mt-14 grid items-start justify-items-center gap-14 sm:grid-cols-2 xl:grid-cols-3 lg:gap-10">
          {VARIANTS.map((v) => (
            <PosterBlock key={v.id} {...v} />
          ))}
        </div>
        <div className="mt-24 text-center">
          <h2 className="font-display text-3xl tracking-tight text-white sm:text-4xl">
            Formats <em className="italic text-gold">Instagram</em>
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-white/50">
            Vos affiches déclinées au format carré (1080 × 1080), prêtes à publier sur Instagram et Facebook.
          </p>
        </div>
        <div className="mt-12 grid items-start justify-items-center gap-14 sm:grid-cols-2 xl:grid-cols-3 lg:gap-10">
          {CARRES.map((v) => (
            <PosterBlock key={v.id} {...v} />
          ))}
        </div>
        <p className="mt-14 text-center">
          <a href="/espace" className="text-[10px] uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-gold">
            ← Retour à l'espace
          </a>
        </p>
      </div>
    </div>
  );
}
